import { visit } from 'unist-util-visit';
/**
 * `{{name}}` in page TEXT, and nowhere else.
 *
 * Operating on the syntax tree rather than on the source string is the whole
 * mechanism: `code` and `inlineCode` are their own node types, so a code
 * sample that SHOWS `{{syntax}}` is never touched — not by substitution and
 * not by the usage count — without a fence-tracking loop that could disagree
 * with the parser about where a fence ends.
 *
 * The name between the braces is the schema's VARIABLE_NAME
 * (`^[a-z][a-z0-9_]*$`, @staleless/schema), restated between literal braces.
 * A brace pair that does not match — `{{ name }}`, `{{Name}}`, Jinja's
 * `{{ loop.index }}` — is not a reference and stays exactly as written.
 */
export const VARIABLE_REFERENCE = /\{\{([a-z][a-z0-9_]*)\}\}/g;
/**
 * Replace defined references in the tree's text nodes, in place.
 *
 * An UNDEFINED name stays literal, deliberately: `{{product_name}}` on the
 * page is readable and self-describing, where an empty string is a silent
 * hole. The lint rule `undefined-variable` is what names it to the writer.
 */
export function substituteVariables(tree, variables) {
    visit(tree, 'text', (node) => {
        if (!node.value.includes('{{'))
            return;
        node.value = node.value.replace(VARIABLE_REFERENCE, (whole, name) => Object.hasOwn(variables, name) ? variables[name] : whole);
    });
}
/**
 * Every variable a document references, defined or not.
 *
 * One walker for three readers — substitution above, the lint rule, and the
 * editor's impact count — so "uses {{name}}" means the same thing to the
 * renderer, the gate and the number on screen. A count that disagreed with
 * what actually substitutes would be a confident wrong answer.
 */
export function variableReferences(tree) {
    const found = new Set();
    visit(tree, 'text', (node) => {
        for (const match of node.value.matchAll(VARIABLE_REFERENCE))
            found.add(match[1]);
    });
    return found;
}
//# sourceMappingURL=variables.js.map